/**
 * Глобальні банери під верхньою панеллю (UX-13; layout.md, розд. 1): видно один — найважливіший
 * за `GLOBAL_BANNER` (немає зв'язку → сесія → нова версія → оголошення → пропозиція легкого
 * режиму → розбіжність поясу → старий браузер). Свої банери оболонки —
 * тут, `<Banner area="global">`; банери модулів і подій — `showBanner()` у `BannerSlot`.
 * «Немає зв'язку» — за фактами мережі (`shell/online.ts`, UX-29), «нова версія» — `VersionBanner`
 * (UX-31). Сплив сесії банера не має: це вхід поверх сторінки (`features/relogin`, UX-30).
 */
import { useLocation } from '@tanstack/react-router';
import { type ComponentType, useEffect, useLayoutEffect, useState } from 'react';
import { shell as t } from '../i18n/uk/shell.ts';
import { afterFirstScreen } from '../perf/first-screen.ts';
import { type LiteReason, liteOfferReason, readSignals } from '../prefs/lite-offer.ts';
import { getPrefs } from '../prefs/prefs.ts';
import { Banner, BannerSlot, GLOBAL_BANNER } from '../ui/notify/index.ts';
import { BrowserUpdateGate } from './BrowserUpdateGate.tsx';
import { liteBanner, loadLiteBanner } from './lite-banner.ts';
import { useOnline } from './online.ts';
import { RegionBanner } from './RegionBanner.tsx';

const decide = (): LiteReason | null =>
  liteOfferReason(getPrefs(), navigator, readSignals(), Date.now());

interface Offer {
  reason: LiteReason | null;
  /** Банер можна малювати: його чанк уже був на сторінці, коли екран малювався. */
  ready: boolean;
}

const offerNow = (reason: LiteReason | null): Offer => ({
  reason,
  ready: reason !== null && liteBanner() !== null,
});

/**
 * Рішення про пропозицію легкого режиму — до малювання: на старті (у першому рендері) і при
 * переході на інший екран (`useLayoutEffect`, той самий кадр, що й новий екран). Посеред роботи банер
 * не з'являється: він зсунув би вміст (CLS, lite-mode.md, розд. 2). Тому чанк банера починає
 * вантажитись, щойно рішення «так», але малюється банер лише разом з екраном: якщо чанк приїхав
 * після кадру поточного екрана, банер чекає наступного переходу (`pathname`).
 */
export function LiteOfferGate() {
  const pathname = useLocation({ select: (l) => l.pathname });
  const [offer, setOffer] = useState<Offer>(() => offerNow(decide()));
  // biome-ignore lint/correctness/useExhaustiveDependencies: перерішуємо лише на новому екрані
  useLayoutEffect(() => {
    setOffer((prev) => {
      const next = offerNow(prev.reason ?? decide());
      return next.reason === prev.reason && next.ready === prev.ready ? prev : next;
    });
  }, [pathname]);
  useEffect(() => {
    if (offer.reason !== null) void loadLiteBanner();
  }, [offer.reason]);
  const Offer = liteBanner();
  if (!offer.ready || offer.reason === null || Offer === null) return null;
  return <Offer reason={offer.reason} />;
}

/** Чанк банера версії; тести підміняють завантаження (`setVersionChunkForTests`). */
let loadVersion = () => import('./VersionBanner.tsx');

/** Лише для тестів: своє завантаження чанка банера версії (лічильник, збій); без аргументу — справжнє. */
export function setVersionChunkForTests(load = () => import('./VersionBanner.tsx')): void {
  loadVersion = load;
}

/**
 * Лінивий вхід банера «Доступна нова версія» (UX-31): перевірка версії й банер — чанк
 * `VersionBanner` після межі першого екрана (бюджет першого екрана, #1244), законний пізній запит
 * (`isLateScript`, e2e/perf). Чанк приходить разом зі стартом перевірки (`watchVersion`), тож банер
 * є, коли збірка зміниться, навіть якщо старі файли вже прибрано; збій іншого чанка до того —
 * перевірка одразу (`platform/chunk-error.ts`). Збій `import()` — повтор на наступному екрані.
 */
function VersionGate() {
  const pathname = useLocation({ select: (l) => l.pathname });
  const [Version, setVersion] = useState<ComponentType | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: новий екран — ще одна спроба після збою
  useEffect(() => {
    if (Version) return;
    let live = true;
    const cancel = afterFirstScreen(() => {
      loadVersion().then(
        (m) => {
          // Раз на вкладку: повторний виклик (оболонка змонтувалась знову) нічого не робить.
          m.watchVersion();
          if (live) setVersion(() => m.VersionBanner);
        },
        () => undefined,
      );
    });
    return () => {
      live = false;
      cancel();
    };
  }, [Version, pathname]);
  return Version ? <Version /> : null;
}

export function GlobalBanners() {
  const online = useOnline();
  return (
    <>
      <Banner
        area="global"
        priority={GLOBAL_BANNER.offline}
        when={!online}
        tone="warn"
        icon="wifi-off"
        className="banner--global"
        testId="banner-offline"
      >
        <span>{t.offline}</span>
      </Banner>
      <VersionGate />
      <LiteOfferGate />
      <RegionBanner />
      <BrowserUpdateGate />
      <BannerSlot area="global" className="banner--global" />
    </>
  );
}
